/**
 * Collection grid mobile dots + horizontal scroll sync.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');

  /**
   * @param {HTMLElement} grid
   * @returns {HTMLElement[]}
   */
  function getSnapMarkers(grid) {
    return Array.from(grid.querySelectorAll(':scope > .collection-grid-tile:nth-child(2n + 1)')).filter(
      (el) => el instanceof HTMLElement
    );
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} grid
   * @returns {HTMLElement}
   */
  function ensureDots(root, grid) {
    let dots = root.querySelector(':scope > .collection-grid-dots');
    if (!(dots instanceof HTMLElement)) {
      dots = document.createElement('nav');
      dots.className = 'collection-grid-dots';
      dots.setAttribute('aria-label', 'Category pages');
    }

    const moreSection = root.querySelector(':scope > .collection-grid-more');
    if (moreSection instanceof HTMLElement) {
      if (dots.nextElementSibling !== moreSection) {
        root.insertBefore(dots, moreSection);
      }
    } else if (grid.nextElementSibling !== dots) {
      grid.insertAdjacentElement('afterend', dots);
    }

    return dots;
  }

  /**
   * Snap markers (odd tiles = column starts) that lie within scroll range.
   * @param {HTMLElement} scroller
   * @param {HTMLElement[]} markers
   * @returns {HTMLElement[]}
   */
  function getReachableMarkers(scroller, markers) {
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 1) return [];

    const tolerance = 2;
    const reachable = markers.filter((marker) => marker.offsetLeft <= maxScroll + tolerance);

    return reachable.filter((marker, idx, arr) => {
      if (idx === 0) return true;
      return Math.abs(marker.offsetLeft - arr[idx - 1].offsetLeft) > tolerance;
    });
  }

  /**
   * @param {HTMLElement} scroller
   * @param {HTMLElement[]} markers
   * @returns {number}
   */
  function getActiveIndex(scroller, markers) {
    if (!markers.length) return 0;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const left = scroller.scrollLeft;

    if (left >= maxScroll - 2) return markers.length - 1;

    let active = 0;
    let distance = Infinity;

    markers.forEach((marker, idx) => {
      const delta = Math.abs(marker.offsetLeft - left);
      if (delta < distance) {
        distance = delta;
        active = idx;
      }
    });

    return active;
  }

  /**
   * @param {HTMLElement} dots
   * @param {number} active
   */
  function setActiveDot(dots, active) {
    dots.querySelectorAll('.collection-grid-dots__dot').forEach((dot, idx) => {
      const on = idx === active;
      dot.classList.toggle('is-active', on);
      if (on) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function initSection(root) {
    const grid = root.querySelector('.collection-links__container--grid');
    if (!(grid instanceof HTMLElement)) return;

    const dots = ensureDots(root, grid);
    const markers = getReachableMarkers(grid, getSnapMarkers(grid));

    dots.replaceChildren();

    if (!MOBILE_MQ.matches || markers.length <= 1) {
      dots.hidden = true;
      return;
    }

    dots.hidden = false;

    markers.forEach((marker, idx) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'collection-grid-dots__dot';
      dot.setAttribute('aria-label', `Go to page ${idx + 1}`);
      dot.addEventListener('click', () => {
        const maxScroll = grid.scrollWidth - grid.clientWidth;
        const isLast = idx === markers.length - 1;
        grid.scrollTo({ left: isLast ? maxScroll : marker.offsetLeft, behavior: 'smooth' });
      });
      dots.appendChild(dot);
    });

    const update = () => setActiveDot(dots, getActiveIndex(grid, markers));
    update();

    if (!grid.dataset.cgDotsBound) {
      grid.dataset.cgDotsBound = 'true';
      grid.addEventListener('scroll', update, { passive: true });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-collection-grid]').forEach((section) => {
      if (section instanceof HTMLElement) initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('resize', initAll, { passive: true });
  MOBILE_MQ.addEventListener('change', initAll);
})();
